import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import LiveScheduleView from './LiveScheduleView';
import ScheduleEntry from '../models/ScheduleEntry';
import { mapScheduleEntryFromApi } from '../mappers/scheduleEntryApiMapper';
import { previewScheduleShare } from '../services/apiService';
import {
    readScheduleSharePreviewComposition,
    clearScheduleSharePreviewComposition
} from '../utils/scheduleSharePreviewStorage';

const ScheduleSharePreviewPage = ({
    user,
    teachers,
    groups,
    subjects,
    rooms,
    isAssetsLoading = false,
    onAddGroup
}) => {
    const [composition, setComposition] = useState(null);
    const [entries, setEntries] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        let mounted = true;

        const load = async () => {
            const stored = readScheduleSharePreviewComposition();
            if (!stored) {
                if (mounted) {
                    setError('No preview composition found. Open Share Schedule and click Preview again.');
                    setLoading(false);
                }
                return;
            }

            setComposition(stored);
            try {
                const result = await previewScheduleShare({
                    startWeek: stored.startWeek,
                    startWeekYear: stored.startWeekYear,
                    endWeek: stored.endWeek,
                    endWeekYear: stored.endWeekYear,
                    periods: stored.periods
                });
                if (!mounted) return;

                const rawEntries = Array.isArray(result?.entries) ? result.entries : [];
                const mapped = rawEntries.map((row) => new ScheduleEntry(mapScheduleEntryFromApi(row)));
                setEntries(mapped);
            } catch (e) {
                if (mounted) setError(e.message || 'Failed to load preview');
            } finally {
                if (mounted) setLoading(false);
            }
        };

        load();
        return () => { mounted = false; };
    }, []);

    const rangeLabel = useMemo(() => {
        if (!composition) return '';
        const { startWeek, startWeekYear, endWeek, endWeekYear } = composition;
        if (startWeekYear === endWeekYear) {
            return `${startWeekYear} W${startWeek} → W${endWeek}`;
        }
        return `${startWeekYear} W${startWeek} → ${endWeekYear} W${endWeek}`;
    }, [composition]);

    const enabledCount = useMemo(() => (
        (composition?.periods || []).filter((p) => p.enabled).length
    ), [composition]);

    return (
        <div className="relative min-h-screen bg-gray-50">
            <div className="sticky top-0 z-[60] flex items-center justify-between gap-3 border-b border-purple-200 bg-purple-50 px-4 py-2.5 shadow-sm">
                <div className="min-w-0">
                    <div className="text-sm font-semibold text-purple-900">Share preview</div>
                    <div className="text-xs text-purple-700 truncate">
                        {rangeLabel || '…'}
                        {enabledCount > 0 ? ` · ${enabledCount} period${enabledCount === 1 ? '' : 's'}` : ''}
                        {' · '}not a public link yet
                    </div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                    <button
                        type="button"
                        className="px-3 py-1.5 text-sm rounded border border-purple-300 text-purple-800 hover:bg-purple-100"
                        onClick={() => {
                            clearScheduleSharePreviewComposition();
                            window.close();
                        }}
                    >
                        Close
                    </button>
                    <Link
                        to="/schedule"
                        className="px-3 py-1.5 text-sm rounded bg-purple-600 text-white hover:bg-purple-700"
                        onClick={() => clearScheduleSharePreviewComposition()}
                    >
                        Back to app
                    </Link>
                </div>
            </div>

            {loading && (
                <div className="p-8 text-center text-gray-600">Loading preview…</div>
            )}
            {error && (
                <div className="m-4 rounded border border-red-200 bg-red-50 p-4 text-sm text-red-700">
                    {error}
                </div>
            )}
            {!loading && !error && entries && (
                <LiveScheduleView
                    user={user}
                    teachers={teachers}
                    groups={groups}
                    subjects={subjects}
                    rooms={rooms}
                    isAssetsLoading={isAssetsLoading}
                    isPublicView={true}
                    isScheduleShare={true}
                    forcedScheduleEntries={entries}
                    onAddGroup={onAddGroup}
                />
            )}
        </div>
    );
};

export default ScheduleSharePreviewPage;
